// shop-frontend/src/context/ShopAuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";

const RAW_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_BASE ||
  "https://moondala-one.onrender.com";

const ShopAuthContext = createContext(null);

const SHOP_TOKEN_KEY = "shopToken";
const SHOP_OBJ_KEY = "shop";

// legacy keys we might see in old builds
const LEGACY_TOKEN_KEY = "token";
const LEGACY_SHOP_KEY = "shopInfo";

/* =========================
   Helpers
========================= */
function normalizeBase(u) {
  const s = String(u || "").trim();
  if (!s) return "https://moondala-one.onrender.com";
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

// prevents ".../api/api/..."
function dedupeApi(url) {
  return String(url || "").replace(/\/api\/api(\/|$)/, "/api$1");
}

function readJsonSafe(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function getShopToken() {
  try {
    // Only read the shop-specific key. Do NOT fall back to the generic "token"
    // key — that key is used by the user-facing app and could be a user JWT,
    // which would cause all shop API calls to authenticate as a user.
    return localStorage.getItem(SHOP_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function setShopSession({ token, shop } = {}) {
  try {
    if (token) {
      localStorage.setItem(SHOP_TOKEN_KEY, String(token));
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      // Clear any previous logout redirect guard so future logouts can redirect
      try { localStorage.removeItem('shop_logout_redirected'); } catch (_) {}
    }

    if (shop && typeof shop === "object") {
      localStorage.setItem(SHOP_OBJ_KEY, JSON.stringify(shop));
      localStorage.setItem("shopId", String(shop._id || shop.id || ""));
      localStorage.setItem("shopEmail", String(shop.shopEmail || shop.email || ""));
    }
  } catch {}
}

function clearShopSession() {
  try {
    localStorage.removeItem(SHOP_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(SHOP_OBJ_KEY);
    localStorage.removeItem("shopId");
    localStorage.removeItem("shopEmail");
    localStorage.removeItem(LEGACY_SHOP_KEY);
  } catch {}
}

function clearOtherRoleSessions() {
  try {
    // user
    localStorage.removeItem("userToken");
    localStorage.removeItem("user");
    // admin
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    // shared flags
    localStorage.removeItem("mode");
    localStorage.removeItem("role");
  } catch {}
}

function pickToken(data) {
  return (
    data?.token ||
    data?.shopToken ||
    data?.accessToken ||
    data?.jwt ||
    data?.authToken ||
    data?.data?.token ||
    data?.data?.shopToken ||
    ""
  );
}

function pickShop(data) {
  return data?.shop || data?.me || data?.data?.shop || data?.data?.me || null;
}

async function safeJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

/**
 * apiShop(path, opts)
 * - pass path like "/api/shop/auth/me"
 * - base comes from env (can include /api or not)
 * - auto adds Bearer token
 */
async function apiShop(path, opts = {}) {
  const token = getShopToken();

  const headers = {
    Accept: "application/json",
    ...(opts.headers || {}),
  };

  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;

  // If body exists and NOT FormData: ensure JSON content-type
  if (opts.body != null && !isForm) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const base = normalizeBase(RAW_BASE);
  const url = dedupeApi(`${base}${path}`);

  const res = await fetch(url, { ...opts, headers });
  const data = await safeJson(res);

  if (!res.ok) {
    const err = new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    err.url = url;
    throw err;
  }

  return data;
}

async function tryMany(calls) {
  let lastErr = null;
  for (const fn of calls) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      // fallback ONLY on 404 (route mismatch)
      if (e?.status && e.status !== 404) throw e;
    }
  }
  throw lastErr || new Error("All endpoints failed");
}

/* =========================
   Provider
========================= */
export function ShopAuthProvider({ children }) {
  const [shop, setShop] = useState(() => {
    // prefer canonical "shop", fallback to old "shopInfo"
    return readJsonSafe(SHOP_OBJ_KEY) || readJsonSafe(LEGACY_SHOP_KEY) || null;
  });

  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  
  // ✅ Theme state (light or dark)
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem("shopTheme") || "dark";
  });

  // ✅ Setup axios interceptor for auth
  useEffect(() => {
    const token = getShopToken();
    
    // Configure axios instance with token and base URL
    const base = normalizeBase(RAW_BASE);
    axios.defaults.baseURL = base;
    
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, []);
  
  // ✅ Apply theme on mount and when it changes
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    document.body.className = theme === "light" ? "light-theme" : "dark-theme";
  }, [theme]);
  
  // ✅ Theme setter function
  const setTheme = useCallback((newTheme) => {
    if (newTheme === "light" || newTheme === "dark") {
      setThemeState(newTheme);
      localStorage.setItem("shopTheme", newTheme);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    const token = getShopToken();
    if (!token) {
      setShop(null);
      return null;
    }

    try {
      const data = await tryMany([
        () => apiShop("/api/shop/auth/me", { method: "GET" }),
        () => apiShop("/api/shops/auth/me", { method: "GET" }),

        // legacy fallbacks
        () => apiShop("/api/shop/me", { method: "GET" }),
        () => apiShop("/api/shops/me", { method: "GET" }),
      ]);

      const fresh = pickShop(data) || data;
      if (!fresh) throw new Error("No shop in /me response");

      setShopSession({ token, shop: fresh });
      setShop(fresh);
      return fresh;
    } catch (e) {
      // ✅ Only clear token if truly invalid
      if (e?.status === 401 || e?.status === 403) {
        clearShopSession();
        setShop(null);
      } else {
        // keep cached shop to avoid forced logout on server hiccups
        const cached = readJsonSafe(SHOP_OBJ_KEY) || readJsonSafe(LEGACY_SHOP_KEY);
        if (cached) setShop(cached);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      setLoading(true);
      await refreshMe();
      if (!alive) return;
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [refreshMe]);

  const login = useCallback(
    async (inputA, inputB) => {
      clearShopSession();
      clearOtherRoleSessions();
      setShop(null);

      let email = "";
      let password = "";

      if (typeof inputA === "object" && inputA) {
        email = inputA.shopEmail || inputA.email || "";
        password = inputA.password || "";
      } else {
        email = inputA || "";
        password = inputB || "";
      }

      email = String(email || "").trim().toLowerCase();
      password = String(password || "").trim();

      if (!email || !password) throw new Error("Email and password are required.");

      // ✅ SUPPORT BOTH BACKENDS:
      // some expect { email }, some expect { shopEmail }
      const payloadA = { email, password };
      const payloadB = { shopEmail: email, password };

      const data = await tryMany([
        () =>
          apiShop("/api/shop/auth/login", {
            method: "POST",
            body: JSON.stringify(payloadA),
          }),
        () =>
          apiShop("/api/shop/auth/login", {
            method: "POST",
            body: JSON.stringify(payloadB),
          }),
        () =>
          apiShop("/api/shops/auth/login", {
            method: "POST",
            body: JSON.stringify(payloadA),
          }),
        () =>
          apiShop("/api/shops/auth/login", {
            method: "POST",
            body: JSON.stringify(payloadB),
          }),

        // legacy fallbacks
        () =>
          apiShop("/api/shop/login", {
            method: "POST",
            body: JSON.stringify(payloadA),
          }),
        () =>
          apiShop("/api/shop/login", {
            method: "POST",
            body: JSON.stringify(payloadB),
          }),
      ]);

      const token = pickToken(data);
      const shopObj = pickShop(data);

      if (!token) throw new Error("Login failed: token missing.");

      // ✅ always store token immediately
      setShopSession({ token, shop: shopObj || null });

      // ✅ Update axios header with new token
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      if (shopObj) {
        setShop(shopObj);
        return;
      }

      // if backend didn't include shop object, fetch it
      await refreshMe();
    },
    [refreshMe]
  );

  const register = useCallback(
    async (payload) => {
      clearShopSession();
      clearOtherRoleSessions();
      setShop(null);

      const body = payload && typeof payload === "object" ? payload : {};

      const data = await tryMany([
        () =>
          apiShop("/api/shop/auth/register", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        () =>
          apiShop("/api/shops/auth/register", {
            method: "POST",
            body: JSON.stringify(body),
          }),

        // legacy fallbacks
        () =>
          apiShop("/api/shop/register", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        () =>
          apiShop("/api/shops/register", {
            method: "POST",
            body: JSON.stringify(body),
          }),
      ]);

      const token = pickToken(data);
      if (!token) throw new Error("Register failed: token missing.");

      setShopSession({ token, shop: pickShop(data) || null });
      
      // ✅ Update axios header with new token
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      await refreshMe();
    },
    [refreshMe]
  );

  const logout = useCallback(() => {
    // Set state IMMEDIATELY to stop all React rendering
    setLoggingOut(true);
    
    // Clear session synchronously
    clearShopSession();
    setShop(null);
    delete axios.defaults.headers.common["Authorization"];
    
    // Force immediate navigation
    // Use absolute URL to ensure we stay on shop domain (not redirect to main user site)
    const shopLoginUrl = window.location.origin + "/shop/login";
    window.location.href = shopLoginUrl;
  }, []);

  const value = useMemo(() => {
    const token = getShopToken();
    return {
      shop,
      loading,
      loggingOut,
      isAuthed: !!token,
      token,
      apiShop,
      refreshMe,
      login,
      register,
      logout,
      theme,
      setTheme,
    };
  }, [shop, loading, loggingOut, refreshMe, login, register, logout, theme, setTheme]);

  return <ShopAuthContext.Provider value={value}>{children}</ShopAuthContext.Provider>;
}

export function useShopAuth() {
  const ctx = useContext(ShopAuthContext);
  // Return a safe default instead of throwing so components can call this
  // hook outside of the provider during builds or tests without crashing.
  if (!ctx) {
    return {
      shop: null,
      loading: false,
      loggingOut: false,
      isAuthed: false,
      token: "",
      apiShop: async () => ({}),
      refreshMe: async () => null,
      login: async () => null,
      register: async () => null,
      logout: () => {},
      theme: "dark",
      setTheme: () => {},
    };
  }
  return ctx;
}
